import React, { useState, useEffect } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  Alert,
  Loader,
  Box,
  Text,
  ActionIcon,
  Paper,
  Center,
  Switch,
  Badge,
  CopyButton,
  Tooltip,
  ScrollArea,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconX,
  IconUpload,
  IconVideo,
  IconPhoto,
  IconCheck,
  IconCopy,
  IconMicrophone,
} from "@tabler/icons-react";
import { getApiEndpoint } from "../../config/config";

function VideoModal({
  isOpen,
  onClose,
  onSuccess,
  mode = "add", // 'add' or 'edit'
  initialData = null,
  videosLength = 0,
  courseId,
  getVerifiedJWT,
}) {
  const [formData, setFormData] = useState({
    title: "",
    video_desc: "",
    thumbnail: null,
    videoFile: null,
    sortOrder: videosLength + 1,
    requirePitch: false,
  });

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && initialData) {
        setFormData({
          title: initialData.video_title || "",
          video_desc: initialData.video_desc || "",
          thumbnail: initialData.video_thumbnail || null,
          videoFile: null,
          requirePitch: initialData.require_pitch || false,
        });
      } else if (mode === "add") {
        setFormData({
          title: "",
          video_desc: "",
          thumbnail: null,
          videoFile: null,
          sortOrder: videosLength + 1,
          requirePitch: false,
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, initialData, videosLength]);

  const handleThumbnailDrag = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleThumbnailDrop = e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith("image/")) {
        setFormData(prev => ({ ...prev, thumbnail: file }));
      }
    }
  };

  const handleThumbnailClick = () => {
    document.getElementById("video-modal-thumbnail-input").click();
  };

  const handleThumbnailSelect = e => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type.startsWith("image/")) {
        setFormData(prev => ({ ...prev, thumbnail: file }));
      }
    }
  };

  const handleVideoFileDrag = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleVideoFileDrop = e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith("video/")) {
        setFormData(prev => ({ ...prev, videoFile: file }));
      }
    }
  };

  const handleVideoFileClick = () => {
    document.getElementById("video-modal-file-input").click();
  };

  const handleVideoFileSelect = e => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type.startsWith("video/")) {
        setFormData(prev => ({ ...prev, videoFile: file }));
      }
    }
  };

  const getThumbnailUrl = thumbnail => {
    if (!thumbnail) return null;
    if (thumbnail instanceof File) {
      return URL.createObjectURL(thumbnail);
    }
    return thumbnail;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Video title is required";
    }

    if (mode === "add") {
      if (!formData.videoFile) {
        newErrors.videoFile = "Video file is required";
      }

      if (!formData.sortOrder || formData.sortOrder < 1) {
        formData.sortOrder = 0;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (mode === "add") {
      await handleAddVideo();
    } else {
      await handleUpdateVideo();
    }
  };

  const handleAddVideo = async () => {
    setIsSaving(true);
    setErrors({});

    try {
      const jwt = await getVerifiedJWT();

      const body = {
        course_id: courseId,
        video_title: formData.title,
        video_desc: formData.video_desc,
        sort_order: formData.sortOrder,
        video_file_name: formData.videoFile.name,
        video_file_content_type: formData.videoFile.type,
        require_pitch: formData.requirePitch,
      };

      const saveRes = await fetch(getApiEndpoint("/v1/training/video/add"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(body),
      });

      if (!saveRes.ok) {
        const errorData = await saveRes.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to save video metadata");
      }

      const responseData = await saveRes.json();

      // Close modal and pass upload info to parent
      handleClose();
      onSuccess({
        video: responseData.data.video,
        uploadUrl: responseData.data.presign.uploadUrl,
        videoFile: formData.videoFile,
      });
    } catch (err) {
      setErrors({
        general: err.message || "Upload failed. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateVideo = async () => {
    setIsSaving(true);
    setErrors({});

    try {
      const jwt = await getVerifiedJWT();

      // Update thumbnail if a new file was selected
      if (formData.thumbnail instanceof File) {
        const thumbnailFormData = new FormData();
        thumbnailFormData.append("video_id", initialData.id);
        thumbnailFormData.append("video_thumbnail", formData.thumbnail);

        const thumbnailResponse = await fetch(
          getApiEndpoint("/v1/training/video/thumbnail"),
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
            body: thumbnailFormData,
          }
        );

        if (!thumbnailResponse.ok) {
          const errorData = await thumbnailResponse.json().catch(() => ({}));
          throw new Error(
            errorData.message || "Failed to update video thumbnail"
          );
        }
      }

      const payload = {
        id: initialData.id,
        title: formData.title,
        video_desc: formData.video_desc,
        require_pitch: formData.requirePitch,
      };

      const response = await fetch(
        getApiEndpoint("/v1/training/video/update"),
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update video");
      }

      const responseData = await response.json();
      onSuccess(responseData.data);
      handleClose();
    } catch (error) {
      setErrors({
        general:
          error.message ||
          "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: "",
      video_desc: "",
      thumbnail: null,
      videoFile: null,
      sortOrder: videosLength + 1,
      requirePitch: false,
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={mode === "add" ? "Add New Video" : "Edit Video"}
      size="lg"
      padding="xl"
    >
      <Stack gap="xl">
        {errors.general && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
          >
            {errors.general}
          </Alert>
        )}

        <TextInput
          label="Video Title"
          placeholder="Enter video title"
          value={formData.title}
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              title: e.target.value,
            }))
          }
          error={errors.title}
          disabled={isSaving}
          required
        />

        <Textarea
          label="Video Description"
          placeholder="Enter video description (optional)"
          value={formData.video_desc}
          onChange={e =>
            setFormData(prev => ({
              ...prev,
              video_desc: e.target.value,
            }))
          }
          disabled={isSaving}
          rows={3}
        />

        <Switch
          label="Require Pitch"
          description="Enable this to require users to submit a pitch for this video"
          checked={formData.requirePitch}
          onChange={event => {
            const checked = event?.currentTarget?.checked ?? false;
            setFormData(prev => ({
              ...prev,
              requirePitch: checked,
            }));
          }}
          disabled={isSaving}
        />

        {mode === "edit" && (
          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Video Thumbnail
            </Text>
            <Paper
              p="md"
              radius="md"
              withBorder
              onClick={handleThumbnailClick}
              style={{
                cursor: "pointer",
                borderColor: errors.thumbnail
                  ? "var(--mantine-color-red-6)"
                  : undefined,
                borderStyle: "dashed",
                minHeight: "120px",
              }}
              onDragEnter={handleThumbnailDrag}
              onDragLeave={handleThumbnailDrag}
              onDragOver={handleThumbnailDrag}
              onDrop={handleThumbnailDrop}
            >
              {formData.thumbnail ? (
                <Box pos="relative">
                  <img
                    src={getThumbnailUrl(formData.thumbnail)}
                    alt="Video thumbnail"
                    style={{
                      width: "100%",
                      maxHeight: "200px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                  {formData.thumbnail instanceof File && (
                    <ActionIcon
                      size="sm"
                      color="red"
                      variant="filled"
                      pos="absolute"
                      top={8}
                      right={8}
                      onClick={e => {
                        e.stopPropagation();
                        setFormData(prev => ({
                          ...prev,
                          thumbnail: null,
                        }));
                        const input = document.getElementById(
                          "video-modal-thumbnail-input"
                        );
                        if (input) input.value = "";
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  )}
                </Box>
              ) : (
                <Center h="100%">
                  <Stack align="center" gap="sm">
                    <IconPhoto size={32} color="gray" />
                    <Text size="sm" c="dimmed" ta="center">
                      Click or drag to upload thumbnail
                    </Text>
                  </Stack>
                </Center>
              )}
            </Paper>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailSelect}
              id="video-modal-thumbnail-input"
              style={{ display: "none" }}
            />
            {errors.thumbnail && (
              <Text size="xs" c="red">
                {errors.thumbnail}
              </Text>
            )}
          </Stack>
        )}

        {/* ── Transcript section (edit mode only) ── */}
        {mode === "edit" && initialData?.transcription_status && (
          <Stack gap="xs">
            <Group gap="xs" align="center">
              <IconMicrophone size={16} />
              <Text size="sm" fw={500}>
                Transcript
              </Text>
              {initialData.transcription_status === "PROCESSING" && (
                <Badge color="yellow" variant="light" size="sm">
                  <Group gap={4} align="center">
                    <Loader size={10} color="yellow" />
                    Generating…
                  </Group>
                </Badge>
              )}
              {initialData.transcription_status === "READY" && (
                <Badge color="green" variant="light" size="sm">
                  Ready
                </Badge>
              )}
              {initialData.transcription_status === "FAILED" && (
                <Badge color="red" variant="light" size="sm">
                  Failed
                </Badge>
              )}
              {initialData.transcription_status === "READY" &&
                initialData.transcription && (
                  <CopyButton value={initialData.transcription} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? "Copied!" : "Copy transcript"}
                        withArrow
                      >
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          color={copied ? "teal" : "gray"}
                          onClick={copy}
                        >
                          {copied ? (
                            <IconCheck size={12} />
                          ) : (
                            <IconCopy size={12} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                )}
            </Group>

            {initialData.transcription_status === "PROCESSING" && (
              <Text size="xs" c="dimmed">
                The transcript is being generated by AWS Transcribe. This
                typically takes 50–80% of the video&apos;s duration. Reload the
                modal once it&apos;s ready.
              </Text>
            )}

            {initialData.transcription_status === "FAILED" && (
              <Alert
                icon={<IconAlertCircle size={14} />}
                color="red"
                variant="light"
                p="xs"
              >
                Transcription failed. Re-upload or contact support if the issue
                persists.
              </Alert>
            )}

            {initialData.transcription_status === "READY" &&
              initialData.transcription && (
                <ScrollArea h={160} type="auto">
                  <Paper
                    p="sm"
                    radius="sm"
                    withBorder
                    style={{ backgroundColor: "var(--mantine-color-gray-0)" }}
                  >
                    <Text
                      size="xs"
                      style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                    >
                      {initialData.transcription}
                    </Text>
                  </Paper>
                </ScrollArea>
              )}

            {initialData.transcription_status === "READY" &&
              !initialData.transcription && (
                <Text size="xs" c="dimmed">
                  Transcript is ready but contains no text.
                </Text>
              )}
          </Stack>
        )}

        {mode === "add" && (
          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Video File{" "}
              <Text span c="red">
                *
              </Text>
            </Text>
            <Paper
              p="md"
              radius="md"
              withBorder
              onClick={handleVideoFileClick}
              style={{
                cursor: "pointer",
                borderColor: errors.videoFile
                  ? "var(--mantine-color-red-6)"
                  : undefined,
                borderStyle: "dashed",
                minHeight: "120px",
              }}
              onDragEnter={handleVideoFileDrag}
              onDragLeave={handleVideoFileDrag}
              onDragOver={handleVideoFileDrag}
              onDrop={handleVideoFileDrop}
            >
              {formData.videoFile ? (
                <Group justify="space-between" align="center">
                  <Group>
                    <IconVideo size={32} />
                    <Stack gap={4}>
                      <Text size="sm" fw={500}>
                        {formData.videoFile.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {(formData.videoFile.size / (1024 * 1024)).toFixed(1)}{" "}
                        MB
                      </Text>
                    </Stack>
                  </Group>
                  <ActionIcon
                    size="sm"
                    color="red"
                    variant="light"
                    onClick={e => {
                      e.stopPropagation();
                      setFormData(prev => ({
                        ...prev,
                        videoFile: null,
                      }));
                      const input = document.getElementById(
                        "video-modal-file-input"
                      );
                      if (input) input.value = "";
                    }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              ) : (
                <Center h="100%">
                  <Stack align="center" gap="sm">
                    <IconUpload size={32} color="gray" />
                    <Text size="sm" c="dimmed" ta="center">
                      Click or drag to upload video
                    </Text>
                  </Stack>
                </Center>
              )}
            </Paper>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoFileSelect}
              id="video-modal-file-input"
              style={{ display: "none" }}
            />
            {errors.videoFile && (
              <Text size="xs" c="red">
                {errors.videoFile}
              </Text>
            )}
          </Stack>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={isSaving}
            leftSection={isSaving ? <Loader size="xs" /> : undefined}
          >
            {mode === "add" ? "Add Video" : "Update Video"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default VideoModal;
